import { useEffect, useState } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import { H2 } from "components/ui/typography/titles/Titles"
import { Form, Input } from "antd"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ValidatePassword } from "components/ui/validatePassword/validatePassword"
import { useValidatePassword } from "hooks/validatePassword.hook"
import { ChangePasswordProvider } from "components/providers/Forms.provider"
import { useChangePassword } from "hooks/api/user/user.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import { PasswordChanged } from "../PasswordChanged/PasswordChanged"
import style from "./ChangePassword.module.scss"

const { changePassword, container, form, inputBlock, title, formItem, label } = style

type Props = { isModalOpen: boolean; setModalClose: () => void }

export const ChangePassword = ({ isModalOpen, setModalClose }: Props) => {
	const [submittable, setSubmittable] = useState(false)
	const [passwordChangedSuccessfully, setPasswordChangedSuccessfully] = useState(false)
	const [closable, setClosable] = useState(true)

	const [useForm] = Form.useForm()
	const values = Form.useWatch([], useForm)
	const validatedPassword = useValidatePassword(values)
	const { mutate: changePasswordMutation, isPending: changePasswordMutationPending } = useChangePassword()
	const { formatError } = useApiErrorFormatter()

	useEffect(() => {
		useForm.validateFields({ validateOnly: true }).then(
			() => {
				const password = values?.password
				const confirmPassword = values?.confirmPassword
				const passwordMatch = password === confirmPassword

				setSubmittable(passwordMatch)
			},
			() => {
				setSubmittable(false)
			},
		)
	}, [values, useForm])

	const setModalCloseHandle = () => {
		setModalClose()
		useForm.resetFields()
	}

	const submitFormHandle = () => {
		changePasswordMutation(values, {
			onSuccess: () => {
				setPasswordChangedSuccessfully(true)
				setClosable(false)
			},
			onError: (e) => {
				formatError(e)
			},
		})
	}

	return (
		<Modal
			isModalOpen={isModalOpen}
			setModalClose={setModalCloseHandle}
			closable={closable}
			maskClosable={closable}
			width="100%"
		>
			<div className={changePassword}>
				<div className={container}>
					{changePasswordMutationPending ? (
						<div data-testid="skeleton">
							<Skeleton rows={14} />
						</div>
					) : (
						<>
							{passwordChangedSuccessfully ? (
								<PasswordChanged />
							) : (
								<>
									<H2 className={title}>Change password</H2>
									<ChangePasswordProvider>
										<Form
											form={useForm}
											className={form}
											initialValues={{ currentPassword: "", password: "", confirmPassword: "" }}
											size="large"
										>
											<div className={inputBlock}>
												<span className={label}>Enter current password</span>
												<Form.Item
													name="currentPassword"
													className={formItem}
													required={true}
													rules={[{ required: true, message: "Please, enter current password" }]}
												>
													<Input.Password placeholder="Current password" />
												</Form.Item>
											</div>
											<div className={inputBlock}>
												<span className={label}>Enter a new password</span>
												<Form.Item
													name="password"
													className={formItem}
													required={true}
													rules={[{ required: true, message: "Please, enter new password" }]}
												>
													<Input.Password placeholder="Password" />
												</Form.Item>
												<Form.Item
													name="confirmPassword"
													className={formItem}
													required={true}
													rules={[
														{ required: true, message: "Please, enter confirm password" },
														({ getFieldValue }) => ({
															validator(_, value) {
																if (!value || getFieldValue("password") === value) {
																	return Promise.resolve()
																}
																return Promise.reject(
																	new Error("Passwords do not match. Please enter the same password in both fields"),
																)
															},
														}),
													]}
												>
													<Input.Password placeholder="Confirm Password" />
												</Form.Item>
											</div>
											<ValidatePassword validate={validatedPassword} />
											<ButtonPrimary onClick={submitFormHandle} submittable={submittable}>
												Continue
											</ButtonPrimary>
										</Form>
									</ChangePasswordProvider>
								</>
							)}
						</>
					)}
				</div>
			</div>
		</Modal>
	)
}
